import { useState, useEffect } from 'react';
import axios from 'axios';
import { createGenerationTask, getGenerationStatus, Generation } from '@/lib/api';
import { useGenerationStore } from '@/store/generation-store';
import { useNotificationStore } from '@/store/notification-store';
import { useUserStore } from '@/store/user-store';
import { toast } from 'sonner';
import { fetchQuote } from '@/lib/pricing-api';
import { buildBackendPayload, getEffectiveModelForUI, checkIsVideoModel, resolveAutoImageModelId } from '@/lib/model-helpers';
import { GenerationSource } from '@/lib/constants';
import { humanizeGenerationError } from '@/lib/error-messages';
import { showInsufficientCreditsToast } from '@/lib/insufficient-credits';

export function useGeneration() {
    const {
        selectedModelId, prompt,
        setIsGenerating, setCurrentTaskId,
        currentTaskId,
        aspectRatio, resolution, outputFormat,
        modelParams, referenceImages,
        selectedStyle,
        addPendingGeneration, updatePendingGeneration, removePendingGeneration
    } = useGenerationStore();

    const { addNotification } = useNotificationStore();
    const { fetchCredits } = useUserStore();

    // Result state
    const [resultUrl, setResultUrl] = useState<string | null>(null);
    const [activeTaskIds, setActiveTaskIds] = useState<string[]>([]);

    const generate = async (promptOverride?: string, options?: { 
        source?: GenerationSource | string, 
        batch_id?: string, 
        parent_generation_id?: string, 
        view?: 'image' | 'video' | 'photoshoot',
        quote?: {
            userPrice: number;
            providerPrice: number;
            pricingVersion: number;
        }
    }) => {
        const promptToUse = promptOverride || prompt;
        const viewMode = options?.view || 'image';
        const effectiveModelId = resolveAutoImageModelId(selectedModelId || '');

        // Determine source
        let source = options?.source;
        if (!source) {
            if (viewMode === 'video') source = GenerationSource.VIDEO_GENERATION;
            else if (viewMode === 'photoshoot') source = GenerationSource.PHOTOSHOOT;
            else source = GenerationSource.IMAGE_GENERATION;
        }

        if (!selectedModelId || !promptToUse) {
            toast.error("Выберите модель и введите промпт");
            return;
        }

        // Pre-flight credit check on the supplied quote, if any. Catches the
        // common case where the caller already fetched a quote and we can
        // bail without touching the API. The post-fetch check below is the
        // real gate when the caller didn't pass a quote.
        const userCredits = useUserStore.getState().credits;
        const presetCost = options?.quote?.userPrice;
        if (presetCost != null && presetCost > 0 && userCredits < presetCost) {
            showInsufficientCreditsToast();
            return;
        }

        try {
            setIsGenerating(true);
            setResultUrl(null);
            // toast.success("Generation started...");

            // Calculate Price (Shared Pricing) or Use Quote
            let userPrice: number | undefined;
            let providerPrice: number | undefined;
            let pricingVersion: number | undefined;

            if (options?.quote) {
                userPrice = options.quote.userPrice;
                providerPrice = options.quote.providerPrice;
                pricingVersion = options.quote.pricingVersion;
            } else {
                try {
                    const pricingResolution = viewMode === 'video'
                        ? ((modelParams?.quality as string) || (modelParams?.resolution as string))
                        : ((modelParams?.quality as string) || resolution);

                    // Pricing rows live on VARIANT ids (e.g.
                    // `gpt-image-2-text-to-image`). Sending the family
                    // id silently fell back to the family-level base
                    // row and skipped the override matrix — same bug
                    // we just fixed in `generator-view.tsx`. Resolve
                    // family → variant via `getEffectiveModelForUI`.
                    const effective = getEffectiveModelForUI({
                        modelId: effectiveModelId,
                        view: viewMode === 'video' ? 'video' : 'image',
                        hasInputImage: referenceImages.length > 0,
                        referenceCount: referenceImages.length,
                        isEdit: false,
                        resolution,
                    });
                    const variantId = effective?.variant?.id || effectiveModelId;

                    const quoteResult = await fetchQuote({
                        kind: viewMode === 'video' ? 'video' : 'image',
                        model_id: variantId,
                        params: {
                            resolution: pricingResolution,
                            // `aspectRatio` lives in top-level state (the
                            // sidebar dropdown writes here, not into
                            // `modelParams`). Without explicitly
                            // including it the override-matrix lookup
                            // misses per-(quality, aspect_ratio) cells.
                            aspect_ratio: aspectRatio,
                            referenceCount: referenceImages.length,
                            durationSeconds: modelParams?.duration ? Number(modelParams.duration) : undefined,
                            ...modelParams,
                        }
                    });

                    userPrice = quoteResult.user_price_credits;
                    providerPrice = quoteResult.provider_cost_credits;
                    pricingVersion = quoteResult.pricing_version;
                } catch (e) {
                    // Pricing service error — fallback to backend calculation
                    // Pricing quote failed — fallback to backend calculation
                }
            }

            // Post-fetch credit gate. Most callers (generator-view, photoshoot
            // sidebar) don't pass a pre-resolved quote, so the early check
            // above sees `presetCost === undefined` and does nothing. By the
            // time we land here we have the real `userPrice` from /pricing/quote
            // — re-read the live balance and bail with the same toast / pricing
            // modal the early branch uses.
            if (userPrice != null && userPrice > 0) {
                const liveCredits = useUserStore.getState().credits;
                if (liveCredits < userPrice) {
                    setIsGenerating(false);
                    setResultUrl(null);
                    showInsufficientCreditsToast();
                    return;
                }
            }

            // 1. Prepare common parameters
            const dimensions = aspectRatio === 'custom' && resolution ? resolution : aspectRatio;
            
            // Resolve Effective Model Variant
            const effectiveResult = getEffectiveModelForUI({
                modelId: effectiveModelId,
                view: viewMode,
                hasInputImage: referenceImages.length > 0,
                referenceCount: referenceImages.length,
                isEdit: false,
                resolution: (modelParams.resolution as string) || resolution
            });

            const variantId = effectiveResult?.variant?.id || selectedModelId;

            // Build strictly typed payload using shared logic
            const rawParams = {
                prompt: promptToUse,
                image_dimensions: dimensions,
                output_format: outputFormat,
                style: selectedStyle,
                ...(referenceImages.length > 0 && { image_url: referenceImages[0] }),
                ...(referenceImages.length > 0 && { image_urls: referenceImages }),
                ...modelParams
            };

            // Explicitly remove any batch size related parameters that might have leaked from persisted state
            if ('num_images' in rawParams) delete rawParams.num_images;
            if ('batch_size' in rawParams) delete rawParams.batch_size;
            if ('max_images' in rawParams) delete rawParams.max_images;
            if ('image_count' in rawParams) delete rawParams.image_count;

            const payload = buildBackendPayload(variantId, promptToUse, rawParams);
            
            // Determine loop count
            const count = 1;
            const batchId = options?.batch_id;
            
            const createdTaskIds: string[] = [];

            // Execute in parallel (synchronous requests from user perspective)
            await Promise.all(Array.from({ length: count }).map(async (_, index) => {
                // Generate temporary ID for immediate optimistic update
                const tempId = `temp-${crypto.randomUUID()}`;
                
                // Add to pending list immediately (Optimistic UI)
                const optimisticItem: Generation = {
                    id: tempId,
                    model_id: payload.model_id,
                    status: 'PENDING',
                    cost: userPrice || 0,
                    prompt: payload.prompt,
                    params: payload.params,
                    created_at: new Date().toISOString(),
                    source: source,
                    batch_id: batchId,
                    // Ensure these fields are present for UI to render correctly
                };

                addPendingGeneration(optimisticItem);

                try {
                    // Send to Backend
                    const data = await createGenerationTask({
                        model_id: payload.model_id,
                        prompt: payload.prompt,
                        params: payload.params,
                        source: source,
                        batch_id: batchId,
                        parent_generation_id: options?.parent_generation_id,
                        user_price_credits: userPrice, // Backend charges per request, so this is correct
                        provider_cost_credits: providerPrice,
                        pricing_version: pricingVersion
                    });

                    const taskId = data.id;
                    createdTaskIds.push(taskId);

                    // Update the pending generation with real ID
                    // We remove the temp one and add the real one to ensure ID consistency
                    removePendingGeneration(tempId);
                    addPendingGeneration({
                        ...optimisticItem,
                        id: taskId,
                        status: 'PENDING'
                    });
                    
                    // Update credits immediately
                    fetchCredits();
                } catch (e) {
                    // Individual generation in batch failed
                    updatePendingGeneration(tempId, { status: 'FAILED' });
                    removePendingGeneration(tempId);

                    // Handle insufficient credits (402) — open pricing modal directly
                    if (axios.isAxiosError(e) && e.response?.status === 402) {
                        window.dispatchEvent(new CustomEvent('open-pricing', {
                            detail: { message: `Для генерации нужно ${userPrice || '?'} кредитов` }
                        }));
                        throw e; // Stop batch
                    }
                }
            }));

            if (createdTaskIds.length > 0) {
                const lastId = createdTaskIds[createdTaskIds.length - 1];
                setCurrentTaskId(lastId);
                setActiveTaskIds(createdTaskIds);
                return lastId; // Return the last ID for compatibility
            } else {
                throw new Error("No tasks created");
            }

        } catch (error) {
            // Don't show generic error if credit error toast was already shown
            if (!(axios.isAxiosError(error) && error.response?.status === 402)) {
                toast.error("Не удалось запустить генерацию");
            }
            setIsGenerating(false);
            // Clean up any ghost pending items from failed batch
            const { pendingGenerations, removePendingGeneration: remove } = useGenerationStore.getState();
            pendingGenerations
                .filter(p => p.status === 'PENDING' && p.id.startsWith('temp-'))
                .forEach(p => remove(p.id));
        }
    };

    // Polling Logic
    useEffect(() => {
        // If we have active batch IDs, use them. Otherwise fallback to currentTaskId if present.
        const idsToPoll = activeTaskIds.length > 0 ? activeTaskIds : (currentTaskId ? [currentTaskId] : []);

        if (idsToPoll.length === 0) return;

        let isCancelled = false;
        let timeoutId: NodeJS.Timeout;
        let pollAttempt = 0;
        const pollStartTime = Date.now();
        const MAX_POLL_MS = 10 * 60 * 1000; // 10 minutes

        // Track completed/failed IDs to stop polling them
        const completedIds = new Set<string>();
        const getPollInterval = () => Math.min(2000 + pollAttempt * 500, 8000); // 2s → 8s max

        const poll = async () => {
            if (isCancelled) return;

            // Timeout guard
            if (Date.now() - pollStartTime > MAX_POLL_MS) {
                toast.error("Генерация занимает слишком много времени. Проверьте историю позже.");
                setIsGenerating(false);
                setCurrentTaskId(null);
                setActiveTaskIds([]);
                return;
            }
            
            try {
                let allFinished = true;
                
                // Poll all IDs that are not yet finished
                await Promise.all(idsToPoll.map(async (id) => {
                    if (completedIds.has(id)) return;
                    
                    try {
                        const statusData = await getGenerationStatus(id);
                        const status = statusData.status;

                        if (status === 'COMPLETED') {
                            completedIds.add(id);

                            // Always update resultUrl to latest completed (shows most recent result)
                            setResultUrl(statusData.result_url);

                            updatePendingGeneration(id, {
                                status: 'COMPLETED',
                                result_url: statusData.result_url
                            });

                            // Determine content type for notification
                            let title = 'Генерация завершена';
                            let message = 'Ваше изображение успешно создано';
                            
                            if (statusData.source === GenerationSource.BACKGROUND_REMOVAL) {
                                title = 'Фон удален';
                                message = 'Обработка изображения завершена';
                            } else if (statusData.source === GenerationSource.UPSCALE) {
                                title = 'Апскейл завершен';
                                message = 'Улучшение изображения завершено';
                            } else if (checkIsVideoModel(statusData.model_id || '') || statusData.source === GenerationSource.VIDEO_GENERATION) {
                                title = 'Видео готово';
                                message = 'Ваше видео успешно создано';
                            }

                            toast.success(title);
                            addNotification({
                                type: 'success',
                                title: title,
                                message: message,
                                metadata: { prompt: statusData.prompt, generationId: id }
                            });
                        } else if (status === 'FAILED') {
                            completedIds.add(id);
                            updatePendingGeneration(id, { status: 'FAILED' });

                            const friendlyError = humanizeGenerationError(statusData.error_message);
                            if (friendlyError.isCreditsError) {
                                showInsufficientCreditsToast();
                            } else {
                                toast.error(friendlyError.title);
                            }
                            addNotification({
                                type: 'error',
                                title: friendlyError.title,
                                message: friendlyError.message,
                                metadata: { prompt: statusData.prompt, generationId: id }
                            });
                        } else {
                            allFinished = false;
                        }
                    } catch (e) {
                        // Poll error — will retry
                        allFinished = false; // Retry
                    }
                }));

                if (allFinished) {
                    setIsGenerating(false);
                    setCurrentTaskId(null);
                    setActiveTaskIds([]); // Clear batch

                    // Only clear prompt/images if at least one generation succeeded (not all failed)
                    const succeededCount = Array.from(completedIds).filter(cid => {
                        const pg = useGenerationStore.getState().pendingGenerations.find(p => p.id === cid);
                        return pg?.status === 'COMPLETED';
                    }).length;
                    if (succeededCount > 0) {
                        const { setPrompt, setReferenceImages } = useGenerationStore.getState();
                        setPrompt('');
                        setReferenceImages([]);
                    }
                } else {
                    pollAttempt++;
                    timeoutId = setTimeout(poll, getPollInterval());
                }
                
            } catch (err) {
                // Poll loop error — retry in 2s
                pollAttempt++;
                if (!isCancelled) timeoutId = setTimeout(poll, getPollInterval());
            }
        };

        timeoutId = setTimeout(poll, 2000); // First poll after 2s

        return () => {
            isCancelled = true;
            clearTimeout(timeoutId);
        };
    }, [activeTaskIds, currentTaskId]);

    return { generate, resultUrl };
}
